const COOKIES_STORAGE_KEY = '1648-chat-cookies-preferences';

export type CookiePreferences = {
  necessary: boolean; // Always true as these are essential
  analytics?: boolean;
  marketing?: boolean;
  accepted: boolean; // Whether the user has made a choice
  timestamp: number; // When the choice was made
};

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  accepted: false,
  timestamp: 0,
};

export class CookiesService {
  private static instance: CookiesService;
  private preferences: CookiePreferences;

  private constructor() {
    this.preferences = this.loadPreferences();
  }

  public static getInstance(): CookiesService {
    if (!CookiesService.instance) {
      CookiesService.instance = new CookiesService();
    }
    return CookiesService.instance;
  }

  private loadPreferences(): CookiePreferences {
    try {
      const stored = localStorage.getItem(COOKIES_STORAGE_KEY);
      if (!stored) return { ...defaultPreferences };

      const parsed = JSON.parse(stored) as CookiePreferences;
      return {
        ...defaultPreferences,
        ...parsed,
      };
    } catch (error) {
      console.error('Error loading cookie preferences:', error);
      return { ...defaultPreferences };
    }
  }

  private savePreferences(): void {
    try {
      localStorage.setItem(COOKIES_STORAGE_KEY, JSON.stringify(this.preferences));
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
    }
  }

  public acceptAll(): void {
    this.preferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      accepted: true,
      timestamp: Date.now(),
    };
    this.savePreferences();
  }

  public acceptSelected(analytics: boolean = false, marketing: boolean = false): void {
    this.preferences = {
      necessary: true,
      analytics,
      marketing,
      accepted: true,
      timestamp: Date.now(),
    };
    this.savePreferences();
  }

  public rejectAll(): void {
    this.preferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      accepted: false,
      timestamp: Date.now(),
    };
    this.savePreferences();
  }

  public hasUserMadeChoice(): boolean {
    return this.preferences.accepted;
  }

  public getPreferences(): CookiePreferences {
    return { ...this.preferences };
  }

  public isAnalyticsAccepted(): boolean {
    return this.preferences.analytics ?? false;
  }

  public isMarketingAccepted(): boolean {
    return this.preferences.marketing ?? false;
  }

  public clearPreferences(): void {
    localStorage.removeItem(COOKIES_STORAGE_KEY);
    this.preferences = { ...defaultPreferences };
  }
}

// Export a singleton instance
export const cookiesService = CookiesService.getInstance();
